<template>
  <VModal :showing="showing" container-classes="w-192" @update:showing="$emit('close')">
    <template #header>
      <h2 class="text-lg font-medium text-gray-900 mb-0">Choose any number of sites</h2>
      <p class="text-sm text-gray-700 mb-0 capitalize">Create 360 Slide</p>
    </template>
    <template #default>
      <VMessage class="mb-4" :show="!!error" banner-type="danger" @dismiss="error = null">
        <div data-testid="error">{{ error }}</div>
      </VMessage>
      <div v-if="hasNoSites" class="text-gray-600 font-light text-center max-w-xs mx-auto mt-10 mb-16">
        <p class="text-xl mb-4">No 360 Sites available yet!</p>
        <p class="mb-0">Please contact your VRIFY support team to track the completion of your 360 sites</p>
      </div>
      <VSearchableTable
        v-else
        ref="tableRef"
        v-model:search="search"
        layout="fixed"
        :auto-load="true"
        :multiple="true"
        :options="sceneList as unknown as Option[]"
        :current-item="selected as unknown as Option"
        :loading="loading"
        :count="count"
        placeholder="Search by keyword"
        @selected="toggleSelected"
        @load-more="scroll"
      >
        <template #headers>
          <th class="pl-6 w-32">&nbsp;</th>
          <th @click="sortOn('title')">
            Site Name
            <FontAwesomeIcon v-if="sortKey === 'title'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
          <th class="pr-6 text-right w-48" @click="sortOn('updated_at')">
            Last Updated
            <FontAwesomeIcon v-if="sortKey === 'updated_at'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
        </template>
        <template #option="{ item }">
          <td class="pl-6">
            <img
              v-if="(item as unknown as Site).file"
              class="rounded-lg border border-gray-300 w-full"
              :src="(item as unknown as Site).file.url"
            />
            <PlaceholderDeck v-else class="rounded-lg" />
          </td>
          <td class="overflow-ellipsis overflow-hidden">{{ (item as unknown as Site).title }}</td>
          <td class="pr-6 text-right font-normal text-sm whitespace-nowrap">
            {{ formatDateTimeMed((item as unknown as Site).updated_at) }}
          </td>
        </template>
      </VSearchableTable>
    </template>
    <template #footer>
      <slot name="footer">
        <div class="flex justify-end gap-4">
          <VButton variant="light" @click="$emit('back')">Back</VButton>
          <VButton variant="copper" data-testid="submit" @click="confirm">
            Import {{ selected.length }} Site{{ selected.length === 1 ? '' : 's' }}
          </VButton>
        </div>
      </slot>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed, watch, toRefs } from 'vue'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'
import type { Site } from '@/entities/site'
import type { CameraData } from '@/entities/model3d'
import type { QueryParameters } from '@/composables/useIndexSearch'
import type { Option } from '@/components/VSearchableThumbnailList.vue'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

interface SiteQuery extends QueryParameters {
  sortKey: 'title' | 'updated_at'
  sortDir: '-' | ''
}
interface ApiQuery {
  sort_by: `${SiteQuery['sortDir']}${SiteQuery['sortKey']}`
  query: string | null
  limit: number
}

const props = defineProps({
  projectId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  showing: {
    type: Boolean,
    default: false,
  },
  currentItem: {
    type: Array as PropType<Site[]>,
    default: () => [],
  },
})

const emit = defineEmits(['choose-site', 'close', 'back'])

const tableRef = ref<any | undefined>()
const selected = ref<Site[]>(props.currentItem)
const error = ref<string | null>(null)
const unfilteredSitesCount = ref(0)
const hasNoSites = computed(() => !unfilteredSitesCount.value && !loading.value)
const sceneList = computed(() => sites.value ?? [])

const {
  results: sites,
  query: search,
  count,
  continuation,
  loading,
  sortKey,
  sortDir,
} = useIndexSearch<CameraData, SiteQuery, ApiQuery>({
  initParams: { limit: 10, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => (props.projectId ? `v2/sites?project_id=${props.projectId}` : undefined),
  finalParameters: ({ query, limit, sortKey, sortDir }) => ({
    sort_by: `${sortDir}${sortKey}`,
    query,
    limit,
  }),
})

const { currentItem, showing } = toRefs(props)

watch(
  currentItem,
  () => {
    // Make sure selected value is within viewport
    tableRef.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
  },
  { flush: 'post' }
)

watch(showing, showing => {
  if (showing) {
    selected.value = props.currentItem
  }
})

watch(
  () => props.currentItem,
  () => {
    // Make sure selected value is within viewport
    tableRef.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
  },
  { flush: 'post' }
)

const unwatch = watch(
  count,
  count => {
    if (!count) return

    unfilteredSitesCount.value = count
    unwatch()
  },
  { immediate: true }
)

function sortOn(key: SiteQuery['sortKey']) {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}

function scroll() {
  if (!loading.value && count.value > sites.value.length) {
    continuation.value++
  }
}

function toggleSelected(item: Site) {
  error.value = null

  const isSelected = selected.value.some(i => i.id === item.id)
  if (isSelected) {
    // deselect item
    selected.value = selected.value.filter(i => i.id !== item.id)
  } else {
    // select item
    selected.value = selected.value.concat(item)
  }
}

function confirm() {
  if (!selected.value.length) {
    error.value = 'Please select at least 1 site to import.'
    return
  }
  emit('choose-site', selected.value)
}
</script>

<style lang="stylus" scoped>
.v-searchable-table
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply text-gray-800 p-4 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
  .option img
    aspect-ratio 16/9
    object-fit cover
</style>
